// src/components/Notes/ShareList.tsx
import React, { useState, useEffect } from 'react';
import { Check, X, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { EncryptionService } from '../../utils/encryption';

const encryption = new EncryptionService();

interface ShareListProps {
  userId: string;
  initialTab?: 'received' | 'shared';
  onViewNote?: (noteId: string) => void;
  // ✅ 新增：需要 DEK 來重加密
  dek?: CryptoKey | null;
  onShareAccepted?: () => void;
}

export const ShareList: React.FC<ShareListProps> = ({
  userId,
  initialTab = 'received',
  onViewNote,
  dek,
  onShareAccepted,
}) => {
  const [receivedShares, setReceivedShares] = useState<any[]>([]);
  const [sharedByMe, setSharedByMe] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'received' | 'shared'>(initialTab);

  const loadShares = async () => {
    setLoading(true);
    try {
      const [receivedRes, sharedRes] = await Promise.all([
        fetch('/api/notes/share-list?type=received', {
          headers: { 'X-User-Id': userId }
        }),
        fetch('/api/notes/share-list?type=shared', {
          headers: { 'X-User-Id': userId }
        })
      ]);

      const receivedData = await receivedRes.json();
      const sharedData = await sharedRes.json();

      if (receivedRes.ok) setReceivedShares(receivedData);
      if (sharedRes.ok) setSharedByMe(sharedData);

    } catch (error) {
      toast.error('載入分享列表失敗');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      loadShares();
    }
  }, [userId]);

  // ✅ 接受分享（自動重加密）
  const handleAccept = async (shareId: string) => {
    if (!dek) {
      toast.error('加密金鑰未初始化，請重新登入');
      return;
    }

    try {
      // 1. 獲取分享記錄的詳細資訊（包含分享密碼）
      const shareRes = await fetch(`/api/notes/share-detail?shareId=${shareId}`, {
        headers: { 'X-User-Id': userId }
      });
      
      if (!shareRes.ok) {
        const err = await shareRes.json();
        throw new Error(err.error || '獲取分享資訊失敗');
      }
      
      const shareData = await shareRes.json();

      // 2. 用分享密碼解密 Note Key
      const passwordKey = await encryption.deriveKeyFromPassword(shareData.sharePassword);
      const noteKeyBase64 = await encryption.decryptWithNoteKey(
        shareData.encryptedNoteKey,
        passwordKey
      );
      const noteKeyBuffer = encryption.base64ToArrayBuffer(noteKeyBase64);

      // 3. 用 User 的 DEK 重新加密 Note Key
      const encryptedNoteKeyForUser = await encryption.encryptNoteKeyWithDEK(
        noteKeyBuffer,
        dek
      );

      // 4. 儲存到後端
      const saveRes = await fetch('/api/notes/share-accept', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Id': userId,
        },
        body: JSON.stringify({
          shareId,
          encryptedNoteKey: encryptedNoteKeyForUser,
        }),
      });

      if (!saveRes.ok) {
        const err = await saveRes.json();
        throw new Error(err.error || '接受分享失敗');
      }

      toast.success('✅ 已接受分享！');
      loadShares();
      if (onShareAccepted) onShareAccepted();

    } catch (error) {
      toast.error(error instanceof Error ? error.message : '操作失敗');
    }
  };

	const handleRespond = async (shareId: string, action: 'accept' | 'reject') => {
		// ✅ 先檢查這個分享是否已經被處理過了
		const share = currentList.find(s => s.share_id === shareId);
		if (share && share.status !== 'pending') {
			toast.info('此分享已處理');
			loadShares();
			return;
		}

		try {
			const response = await fetch(`/api/notes/share-respond?shareId=${shareId}`, {
				method: 'PUT',
				headers: {
					'Content-Type': 'application/json',
					'X-User-Id': userId
				},
				body: JSON.stringify({ action })
			});

			const data = await response.json();
			
			if (!response.ok) {
				// ✅ 如果後端回傳錯誤，但狀態可能已經變了，重新載入確認
				await loadShares();
				
				// ✅ 重新檢查狀態
				const updatedShare = receivedShares.find(s => s.share_id === shareId);
				if (updatedShare && updatedShare.status === 'accepted') {
					toast.success('✅ 已接受分享！');
					return;
				}
				throw new Error(data.error || '操作失敗');
			}

			toast.success(action === 'accept' ? '✅ 已接受分享！' : '已拒絕分享');
			loadShares();

		} catch (error) {
			// ✅ 錯誤時也重新載入，確保狀態正確
			const msg = error instanceof Error ? error.message : '操作失敗';
			// 如果錯誤訊息包含「已接受」或「已處理」，顯示成功
			if (msg.includes('已接受') || msg.includes('已處理')) {
				toast.success('✅ 已接受分享！');
			} else {
				toast.error(msg);
			}
			loadShares();
		}
	};

  // 拒絕分享
  const handleReject = async (shareId: string) => {
    try {
      const response = await fetch(`/api/notes/share-respond?shareId=${shareId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-User-Id': userId
        },
        body: JSON.stringify({ action: 'reject' })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || '操作失敗');

      toast.success('已拒絕分享');
      loadShares();

    } catch (error) {
      toast.error(error instanceof Error ? error.message : '操作失敗');
    }
  };

  // 撤銷分享
  const handleRevoke = async (shareId: string) => {
    if (!confirm('確定要撤銷此分享嗎？')) return;

    try {
      const response = await fetch(`/api/notes/revoke-share?shareId=${shareId}`, {
        method: 'DELETE',
        headers: { 'X-User-Id': userId }
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || '撤銷失敗');

      toast.success('已撤銷分享');
      loadShares();

    } catch (error) {
      toast.error(error instanceof Error ? error.message : '撤銷失敗');
    }
  };

  const handleViewNote = (noteId: string) => {
    if (onViewNote) {
      onViewNote(noteId);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">⏳ 待確認</span>;
      case 'accepted':
        return <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">✅ 已接受</span>;
      case 'rejected':
        return <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">❌ 已拒絕</span>;
      case 'revoked':
        return <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400">🚫 已撤銷</span>;
      default:
        return null;
    }
  };

  const renderShareItem = (share: any, type: 'received' | 'shared') => {
    const isPending = share.status === 'pending';
    const isAccepted = share.status === 'accepted';

    return (
      <div
        key={share.share_id}
        onClick={() => handleViewNote(share.note_id)}
        className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
      >
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-gray-900 dark:text-white truncate">
                {share.title || '未命名筆記'}
              </span>
              {getStatusBadge(share.status)}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {type === 'received' ? (
                <span>👤 來自：{share.owner_name}</span>
              ) : (
                <span>👤 分享給：{share.target_name}</span>
              )}
              <span className="mx-2">·</span>
              <span>{new Date(share.created_at).toLocaleDateString()}</span>
              {share.responded_at && (
                <>
                  <span className="mx-2">·</span>
                  <span>回應於：{new Date(share.responded_at).toLocaleDateString()}</span>
                </>
              )}
            </div>
            {share.permission === 'write' && (
              <span className="text-xs text-blue-600 dark:text-blue-400 mt-1 inline-block">
                ✏️ 可編輯
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 ml-4" onClick={(e) => e.stopPropagation()}>
            {type === 'received' && isPending && (
              <>
                <button
                  onClick={() => handleAccept(share.share_id)}
                  className="p-1.5 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50 rounded-lg transition-colors"
                  title="接受"
                >
                  <Check className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleReject(share.share_id)}
                  className="p-1.5 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50 rounded-lg transition-colors"
                  title="拒絕"
                >
                  <X className="w-4 h-4" />
                </button>
              </>
            )}
            {type === 'shared' && isAccepted && (
              <button
                onClick={() => handleRevoke(share.share_id)}
                className="p-1.5 bg-gray-100 dark:bg-gray-700 text-gray-500 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                title="撤銷分享"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
            {type === 'shared' && isPending && (
              <span className="text-xs text-gray-400">等待回應中...</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <div className="text-gray-500 dark:text-gray-400">載入中...</div>
      </div>
    );
  }

  const currentList = activeTab === 'received' ? receivedShares : sharedByMe;
  const hasItems = currentList.length > 0;

  return (
    <div className="space-y-4">
      {/* Tab 切換 */}
      <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setActiveTab('received')}
          className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
            activeTab === 'received'
              ? 'border-blue-500 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          📥 分享給我 ({receivedShares.length})
        </button>
        <button
          onClick={() => setActiveTab('shared')}
          className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
            activeTab === 'shared'
              ? 'border-blue-500 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          📤 我分享的 ({sharedByMe.length})
        </button>
      </div>

      {/* 列表 */}
      {hasItems ? (
        <div className="space-y-3">
          {currentList.map((share) => renderShareItem(share, activeTab))}
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <div className="text-4xl mb-3">
            {activeTab === 'received' ? '📭' : '📪'}
          </div>
          <p>
            {activeTab === 'received'
              ? '還沒有任何人分享筆記給你'
              : '你還沒有分享任何筆記'}
          </p>
        </div>
      )}
    </div>
  );
};